export const EXCALIDRAW_SCHEMA_PACKAGE_VERSION = "0.18.1";
const ORDER_KEY_DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
function orderKeyIntegerLength(head) {
    if (head >= "a" && head <= "z")
        return head.charCodeAt(0) - "a".charCodeAt(0) + 2;
    if (head >= "A" && head <= "Z")
        return "Z".charCodeAt(0) - head.charCodeAt(0) + 2;
    return null;
}
export function isValidExcalidrawOrderKey(value) {
    if (value === null)
        return true;
    if (typeof value !== "string" || value.length === 0)
        return false;
    const integerLength = orderKeyIntegerLength(value[0]);
    if (integerLength === null || value.length < integerLength)
        return false;
    if ([...value.slice(1)].some((digit) => !ORDER_KEY_DIGITS.includes(digit)))
        return false;
    if (value === `A${"0".repeat(26)}`)
        return false;
    const fractionalPart = value.slice(integerLength);
    return !fractionalPart.endsWith("0");
}
const orderKeyPatterns = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"].map((head) => {
    const integerLength = orderKeyIntegerLength(head);
    const integerDigits = integerLength - 1;
    const minimumGuard = head === "A" ? `(?!0{${integerDigits}}$)` : "";
    return {
        pattern: `^${head}${minimumGuard}[0-9A-Za-z]{${integerDigits}}(?:[0-9A-Za-z]*[1-9A-Za-z])?$`,
    };
});
const pointSchema = {
    type: "array",
    prefixItems: [{ type: "number" }, { type: "number" }],
    minItems: 2,
    maxItems: 2,
};
const nullableString = {
    anyOf: [{ type: "string" }, { type: "null" }],
};
const nullablePoint = {
    anyOf: [{ $ref: "#/$defs/point" }, { type: "null" }],
};
const baseElementProperties = {
    id: { type: "string", minLength: 1, description: "Stable unique element identifier." },
    type: {
        enum: [
            "rectangle",
            "diamond",
            "ellipse",
            "line",
            "arrow",
            "freedraw",
            "text",
            "image",
            "frame",
            "magicframe",
            "embeddable",
            "iframe",
        ],
    },
    x: { type: "number", description: "Scene-space X coordinate." },
    y: { type: "number", description: "Scene-space Y coordinate." },
    width: { type: "number", minimum: 0 },
    height: { type: "number", minimum: 0 },
    angle: { type: "number", description: "Rotation in radians." },
    strokeColor: { type: "string" },
    backgroundColor: { type: "string" },
    fillStyle: { enum: ["solid", "hachure", "cross-hatch", "zigzag"] },
    strokeWidth: { type: "number", minimum: 0 },
    strokeStyle: { enum: ["solid", "dashed", "dotted"] },
    roughness: { type: "number", minimum: 0 },
    opacity: { type: "number", minimum: 0, maximum: 100 },
    seed: { type: "integer" },
    version: { type: "integer", minimum: 1 },
    versionNonce: { type: "integer" },
    index: {
        anyOf: [{ type: "string", anyOf: orderKeyPatterns }, { type: "null" }],
        description: "Valid fractional ordering key used for z-order reconciliation; null before ordering.",
    },
    isDeleted: { type: "boolean" },
    groupIds: { type: "array", items: { type: "string" } },
    frameId: nullableString,
    boundElements: {
        anyOf: [
            {
                type: "array",
                items: { $ref: "#/$defs/boundElement" },
            },
            { type: "null" },
        ],
    },
    link: nullableString,
    locked: { type: "boolean" },
    updated: { type: "integer", minimum: 0, description: "Unix timestamp in milliseconds." },
    roundness: {
        anyOf: [{ $ref: "#/$defs/roundness" }, { type: "null" }],
    },
    customData: {
        anyOf: [{ type: "object", additionalProperties: true }, { type: "null" }],
    },
};
const baseRequired = [
    "id",
    "type",
    "x",
    "y",
    "width",
    "height",
    "angle",
    "strokeColor",
    "backgroundColor",
    "fillStyle",
    "strokeWidth",
    "strokeStyle",
    "roughness",
    "opacity",
    "seed",
    "version",
    "versionNonce",
    "index",
    "isDeleted",
    "groupIds",
    "frameId",
    "boundElements",
    "link",
    "locked",
    "updated",
    "roundness",
];
const arrowheadSchema = {
    anyOf: [
        {
            enum: [
                "arrow",
                "bar",
                "dot",
                "circle",
                "circle_outline",
                "triangle",
                "triangle_outline",
                "diamond",
                "diamond_outline",
                "crowfoot_one",
                "crowfoot_many",
                "crowfoot_one_or_many",
            ],
        },
        { type: "null" },
    ],
};
function elementVariant(type, properties = {}, required = []) {
    return {
        allOf: [
            { $ref: "#/$defs/baseElement" },
            {
                type: "object",
                properties: {
                    type: { const: type },
                    ...properties,
                },
                required: ["type", ...required],
                additionalProperties: true,
            },
        ],
    };
}
export const EXCALIDRAW_SCHEMA_DEFS = {
    point: pointSchema,
    roundness: {
        type: "object",
        required: ["type"],
        properties: {
            type: { type: "integer", enum: [1, 2, 3] },
            value: { type: "number", minimum: 0 },
        },
        additionalProperties: true,
    },
    boundElement: {
        type: "object",
        required: ["id", "type"],
        properties: {
            id: { type: "string", minLength: 1 },
            type: { enum: ["arrow", "text"] },
        },
        additionalProperties: true,
    },
    pointBinding: {
        type: "object",
        required: ["elementId", "focus", "gap"],
        properties: {
            elementId: { type: "string", minLength: 1 },
            focus: { type: "number", minimum: -1, maximum: 1 },
            gap: { type: "number" },
            fixedPoint: nullablePoint,
        },
        additionalProperties: true,
    },
    baseElement: {
        type: "object",
        properties: baseElementProperties,
        required: baseRequired,
        additionalProperties: true,
    },
    rectangleElement: elementVariant("rectangle"),
    diamondElement: elementVariant("diamond"),
    ellipseElement: elementVariant("ellipse"),
    lineElement: elementVariant("line", {
        points: { type: "array", minItems: 2, items: { $ref: "#/$defs/point" } },
        lastCommittedPoint: nullablePoint,
        startBinding: {
            anyOf: [{ $ref: "#/$defs/pointBinding" }, { type: "null" }],
        },
        endBinding: {
            anyOf: [{ $ref: "#/$defs/pointBinding" }, { type: "null" }],
        },
        startArrowhead: arrowheadSchema,
        endArrowhead: arrowheadSchema,
    }, [
        "points",
        "lastCommittedPoint",
        "startBinding",
        "endBinding",
        "startArrowhead",
        "endArrowhead",
    ]),
    arrowElement: elementVariant("arrow", {
        points: { type: "array", minItems: 2, items: { $ref: "#/$defs/point" } },
        lastCommittedPoint: nullablePoint,
        startBinding: {
            anyOf: [{ $ref: "#/$defs/pointBinding" }, { type: "null" }],
        },
        endBinding: {
            anyOf: [{ $ref: "#/$defs/pointBinding" }, { type: "null" }],
        },
        startArrowhead: arrowheadSchema,
        endArrowhead: arrowheadSchema,
        elbowed: { type: "boolean" },
        fixedSegments: {
            anyOf: [
                {
                    type: "array",
                    items: {
                        type: "object",
                        required: ["start", "end", "index"],
                        properties: {
                            start: { $ref: "#/$defs/point" },
                            end: { $ref: "#/$defs/point" },
                            index: { type: "integer", minimum: 0 },
                        },
                        additionalProperties: true,
                    },
                },
                { type: "null" },
            ],
        },
        startIsSpecial: { anyOf: [{ type: "boolean" }, { type: "null" }] },
        endIsSpecial: { anyOf: [{ type: "boolean" }, { type: "null" }] },
    }, [
        "points",
        "lastCommittedPoint",
        "startBinding",
        "endBinding",
        "startArrowhead",
        "endArrowhead",
        "elbowed",
    ]),
    freedrawElement: elementVariant("freedraw", {
        points: { type: "array", minItems: 2, items: { $ref: "#/$defs/point" } },
        pressures: {
            type: "array",
            items: { type: "number", minimum: 0, maximum: 1 },
        },
        simulatePressure: { type: "boolean" },
        lastCommittedPoint: nullablePoint,
    }, ["points", "pressures", "simulatePressure", "lastCommittedPoint"]),
    textElement: elementVariant("text", {
        text: { type: "string" },
        originalText: { type: "string" },
        fontSize: { type: "number", exclusiveMinimum: 0 },
        fontFamily: { type: "integer", enum: [1, 2, 3, 5, 6, 7, 8, 9] },
        textAlign: { enum: ["left", "center", "right"] },
        verticalAlign: { enum: ["top", "middle", "bottom"] },
        containerId: nullableString,
        autoResize: { type: "boolean" },
        lineHeight: { type: "number", exclusiveMinimum: 0 },
    }, [
        "text",
        "originalText",
        "fontSize",
        "fontFamily",
        "textAlign",
        "verticalAlign",
        "containerId",
        "autoResize",
        "lineHeight",
    ]),
    imageCrop: {
        type: "object",
        required: ["x", "y", "width", "height", "naturalWidth", "naturalHeight"],
        properties: {
            x: { type: "number" },
            y: { type: "number" },
            width: { type: "number", minimum: 0 },
            height: { type: "number", minimum: 0 },
            naturalWidth: { type: "number", minimum: 0 },
            naturalHeight: { type: "number", minimum: 0 },
        },
        additionalProperties: true,
    },
    imageElement: elementVariant("image", {
        fileId: nullableString,
        status: { enum: ["pending", "saved", "error"] },
        scale: {
            type: "array",
            prefixItems: [{ type: "number" }, { type: "number" }],
            minItems: 2,
            maxItems: 2,
        },
        crop: {
            anyOf: [{ $ref: "#/$defs/imageCrop" }, { type: "null" }],
        },
    }, ["fileId", "status", "scale", "crop"]),
    frameElement: elementVariant("frame", { name: nullableString }, ["name"]),
    magicFrameElement: elementVariant("magicframe", { name: nullableString }, ["name"]),
    embeddableElement: elementVariant("embeddable", {
        link: nullableString,
        validated: { type: "boolean" },
    }, ["link"]),
    iframeElement: elementVariant("iframe", { link: nullableString }, ["link"]),
    excalidrawElement: {
        oneOf: [
            { $ref: "#/$defs/rectangleElement" },
            { $ref: "#/$defs/diamondElement" },
            { $ref: "#/$defs/ellipseElement" },
            { $ref: "#/$defs/lineElement" },
            { $ref: "#/$defs/arrowElement" },
            { $ref: "#/$defs/freedrawElement" },
            { $ref: "#/$defs/textElement" },
            { $ref: "#/$defs/imageElement" },
            { $ref: "#/$defs/frameElement" },
            { $ref: "#/$defs/magicFrameElement" },
            { $ref: "#/$defs/embeddableElement" },
            { $ref: "#/$defs/iframeElement" },
        ],
    },
    elementPatch: {
        type: "object",
        description: "Partial serialized element fields to merge. Element id and type are immutable.",
        properties: {
            ...baseElementProperties,
            id: false,
            type: false,
            points: { type: "array", items: { $ref: "#/$defs/point" } },
            lastCommittedPoint: nullablePoint,
            startBinding: {
                anyOf: [{ $ref: "#/$defs/pointBinding" }, { type: "null" }],
            },
            endBinding: {
                anyOf: [{ $ref: "#/$defs/pointBinding" }, { type: "null" }],
            },
            startArrowhead: arrowheadSchema,
            endArrowhead: arrowheadSchema,
            elbowed: { type: "boolean" },
            fixedSegments: {
                anyOf: [{ type: "array", items: { type: "object" } }, { type: "null" }],
            },
            startIsSpecial: { anyOf: [{ type: "boolean" }, { type: "null" }] },
            endIsSpecial: { anyOf: [{ type: "boolean" }, { type: "null" }] },
            pressures: { type: "array", items: { type: "number" } },
            simulatePressure: { type: "boolean" },
            text: { type: "string" },
            originalText: { type: "string" },
            fontSize: { type: "number", exclusiveMinimum: 0 },
            fontFamily: { type: "integer", enum: [1, 2, 3, 5, 6, 7, 8, 9] },
            textAlign: { enum: ["left", "center", "right"] },
            verticalAlign: { enum: ["top", "middle", "bottom"] },
            containerId: nullableString,
            autoResize: { type: "boolean" },
            lineHeight: { type: "number", exclusiveMinimum: 0 },
            fileId: nullableString,
            status: { enum: ["pending", "saved", "error"] },
            scale: {
                type: "array",
                prefixItems: [{ type: "number" }, { type: "number" }],
                minItems: 2,
                maxItems: 2,
            },
            crop: {
                anyOf: [{ $ref: "#/$defs/imageCrop" }, { type: "null" }],
            },
            name: nullableString,
            validated: { type: "boolean" },
        },
        additionalProperties: true,
    },
    serializedAppState: {
        type: "object",
        properties: {
            viewBackgroundColor: { type: "string" },
            gridSize: {
                anyOf: [{ type: "number", exclusiveMinimum: 0 }, { type: "null" }],
            },
            gridStep: { type: "number", exclusiveMinimum: 0 },
            gridModeEnabled: { type: "boolean" },
            viewModeEnabled: { type: "boolean" },
            zenModeEnabled: { type: "boolean" },
            exportBackground: { type: "boolean" },
            exportEmbedScene: { type: "boolean" },
            exportWithDarkMode: { type: "boolean" },
            name: { type: "string" },
            theme: { enum: ["light", "dark"] },
        },
        additionalProperties: false,
    },
    binaryFile: {
        type: "object",
        required: ["id", "mimeType", "dataURL", "created"],
        properties: {
            id: { type: "string", minLength: 1 },
            mimeType: {
                enum: [
                    "image/png",
                    "image/jpeg",
                    "image/svg+xml",
                    "image/gif",
                    "image/webp",
                    "image/bmp",
                    "image/x-icon",
                    "image/jfif",
                    "application/octet-stream",
                ],
            },
            dataURL: { type: "string", pattern: "^data:" },
            created: { type: "integer", minimum: 0 },
            lastRetrieved: { type: "integer", minimum: 0 },
            version: { type: "integer", minimum: 1 },
        },
        additionalProperties: true,
    },
    binaryFiles: {
        type: "object",
        additionalProperties: { $ref: "#/$defs/binaryFile" },
    },
    excalidrawScene: {
        type: "object",
        required: ["type", "version", "elements"],
        properties: {
            type: { const: "excalidraw" },
            version: { type: "integer", const: 2 },
            source: { type: "string" },
            elements: {
                type: "array",
                items: { $ref: "#/$defs/excalidrawElement" },
            },
            appState: { $ref: "#/$defs/serializedAppState" },
            files: { $ref: "#/$defs/binaryFiles" },
            comments: { type: "array", items: { type: "object", additionalProperties: true } },
        },
        additionalProperties: true,
    },
};
export const EXCALIDRAW_SCENE_SCHEMA = {
    $ref: "#/$defs/excalidrawScene",
    $defs: EXCALIDRAW_SCHEMA_DEFS,
};
export const EXCALIDRAW_ELEMENT_SCHEMA = {
    $ref: "#/$defs/excalidrawElement",
    $defs: EXCALIDRAW_SCHEMA_DEFS,
};
export const EMPTY_EXCALIDRAW_SCENE = {
    type: "excalidraw",
    version: 2,
    source: "excalidraw-ms-mcp",
    elements: [],
    appState: { viewBackgroundColor: "#ffffff" },
    files: {},
};
export const EXCALIDRAW_SCENE_EXAMPLES = [
    EMPTY_EXCALIDRAW_SCENE,
    {
        type: "excalidraw",
        version: 2,
        source: "excalidraw-ms-mcp",
        elements: [
            {
                id: "shape-1",
                type: "rectangle",
                x: 100,
                y: 100,
                width: 240,
                height: 120,
                angle: 0,
                strokeColor: "#1e1e1e",
                backgroundColor: "#a5d8ff",
                fillStyle: "solid",
                strokeWidth: 2,
                strokeStyle: "solid",
                roughness: 1,
                opacity: 100,
                seed: 12345,
                version: 1,
                versionNonce: 67890,
                index: "a0",
                isDeleted: false,
                groupIds: [],
                frameId: null,
                boundElements: null,
                link: null,
                locked: false,
                updated: 1787200000000,
                roundness: { type: 3 },
            },
        ],
        appState: { viewBackgroundColor: "#ffffff" },
        files: {},
    },
];
function isPlainObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
}
function isSafeInteger(value) {
    return Number.isSafeInteger(value);
}
function isPoint(value) {
    return Array.isArray(value) && value.length === 2 && value.every(isNumber);
}
function isRoundness(value) {
    return (value === null ||
        (isPlainObject(value) &&
            Number.isInteger(value.type) &&
            [1, 2, 3].includes(value.type) &&
            (value.value === undefined || (isNumber(value.value) && value.value >= 0))));
}
function isImageCrop(value) {
    return (value === null ||
        (isPlainObject(value) &&
            isNumber(value.x) &&
            isNumber(value.y) &&
            isNumber(value.width) &&
            value.width >= 0 &&
            isNumber(value.height) &&
            value.height >= 0 &&
            isNumber(value.naturalWidth) &&
            value.naturalWidth >= 0 &&
            isNumber(value.naturalHeight) &&
            value.naturalHeight >= 0));
}
const FILL_STYLES = new Set(["solid", "hachure", "cross-hatch", "zigzag"]);
const STROKE_STYLES = new Set(["solid", "dashed", "dotted"]);
const FONT_FAMILIES = new Set([1, 2, 3, 5, 6, 7, 8, 9]);
const ARROWHEADS = new Set([
    "arrow",
    "bar",
    "dot",
    "circle",
    "circle_outline",
    "triangle",
    "triangle_outline",
    "diamond",
    "diamond_outline",
    "crowfoot_one",
    "crowfoot_many",
    "crowfoot_one_or_many",
]);
function isArrowhead(value) {
    return value === null || (typeof value === "string" && ARROWHEADS.has(value));
}
function hasBaseElementFields(element) {
    return (typeof element.id === "string" &&
        element.id.length > 0 &&
        typeof element.type === "string" &&
        isNumber(element.x) &&
        isNumber(element.y) &&
        isNumber(element.width) &&
        isNumber(element.height) &&
        isNumber(element.angle) &&
        typeof element.strokeColor === "string" &&
        typeof element.backgroundColor === "string" &&
        typeof element.fillStyle === "string" &&
        FILL_STYLES.has(element.fillStyle) &&
        isNumber(element.strokeWidth) &&
        typeof element.strokeStyle === "string" &&
        STROKE_STYLES.has(element.strokeStyle) &&
        isNumber(element.roughness) &&
        isNumber(element.opacity) &&
        element.opacity >= 0 &&
        element.opacity <= 100 &&
        isSafeInteger(element.seed) &&
        isSafeInteger(element.version) &&
        isSafeInteger(element.versionNonce) &&
        isValidExcalidrawOrderKey(element.index) &&
        typeof element.isDeleted === "boolean" &&
        Array.isArray(element.groupIds) &&
        (typeof element.frameId === "string" || element.frameId === null) &&
        (Array.isArray(element.boundElements) || element.boundElements === null) &&
        (typeof element.link === "string" || element.link === null) &&
        typeof element.locked === "boolean" &&
        isSafeInteger(element.updated) &&
        isRoundness(element.roundness));
}
export function isValidSerializedExcalidrawElement(value) {
    if (!isPlainObject(value) || !hasBaseElementFields(value))
        return false;
    switch (value.type) {
        case "rectangle":
        case "diamond":
        case "ellipse":
            return true;
        case "line":
        case "arrow":
            return (Array.isArray(value.points) &&
                value.points.length >= 2 &&
                value.points.every(isPoint) &&
                (value.lastCommittedPoint === null || isPoint(value.lastCommittedPoint)) &&
                (value.startBinding === null || isPlainObject(value.startBinding)) &&
                (value.endBinding === null || isPlainObject(value.endBinding)) &&
                isArrowhead(value.startArrowhead) &&
                isArrowhead(value.endArrowhead) &&
                (value.type !== "arrow" || typeof value.elbowed === "boolean"));
        case "freedraw":
            return (Array.isArray(value.points) &&
                value.points.length >= 2 &&
                value.points.every(isPoint) &&
                Array.isArray(value.pressures) &&
                value.pressures.every(isNumber) &&
                typeof value.simulatePressure === "boolean");
        case "text":
            return (typeof value.text === "string" &&
                typeof value.originalText === "string" &&
                isNumber(value.fontSize) &&
                Number.isInteger(value.fontFamily) &&
                FONT_FAMILIES.has(value.fontFamily) &&
                ["left", "center", "right"].includes(value.textAlign) &&
                ["top", "middle", "bottom"].includes(value.verticalAlign) &&
                (typeof value.containerId === "string" || value.containerId === null) &&
                typeof value.autoResize === "boolean" &&
                isNumber(value.lineHeight));
        case "image":
            return ((typeof value.fileId === "string" || value.fileId === null) &&
                ["pending", "saved", "error"].includes(value.status) &&
                Array.isArray(value.scale) &&
                value.scale.length === 2 &&
                value.scale.every(isNumber) &&
                isImageCrop(value.crop));
        case "frame":
        case "magicframe":
            return typeof value.name === "string" || value.name === null;
        case "embeddable":
        case "iframe":
            return typeof value.link === "string" || value.link === null;
        default:
            return false;
    }
}
export function isValidStructuredExcalidrawScene(data) {
    if (!isPlainObject(data))
        return false;
    if (data.type !== "excalidraw" ||
        data.version !== 2 ||
        !Array.isArray(data.elements) ||
        (data.appState !== undefined && !isPlainObject(data.appState)) ||
        (data.files !== undefined && !isPlainObject(data.files))) {
        return false;
    }
    if (!data.elements.every(isValidSerializedExcalidrawElement))
        return false;
    return Object.values(data.files ?? {}).every((file) => isPlainObject(file) &&
        typeof file.id === "string" &&
        typeof file.mimeType === "string" &&
        typeof file.dataURL === "string" &&
        file.dataURL.startsWith("data:") &&
        isSafeInteger(file.created) &&
        file.created >= 0);
}
const MAX_INTEGRITY_ISSUES = 20;
function elementPath(index) {
    return `scene.elements[${index}]`;
}
function activeElement(element) {
    return element.isDeleted !== true;
}
/**
 * Validate relationships and persistence hazards JSON Schema cannot express.
 *
 * Callers should run structural validation first. This function remains defensive
 * because it is also used to produce bounded diagnostics for malformed agent input.
 */
export function validateExcalidrawSceneIntegrity(data, options = {}) {
    const requestedMaxIssues = options.maxIssues ?? MAX_INTEGRITY_ISSUES;
    const maxIssues = requestedMaxIssues === Number.POSITIVE_INFINITY ||
        (Number.isInteger(requestedMaxIssues) && requestedMaxIssues > 0)
        ? requestedMaxIssues
        : MAX_INTEGRITY_ISSUES;
    const issues = [];
    let valid = true;
    let truncated = false;
    const add = (issue) => {
        valid = false;
        if (issues.length < maxIssues)
            issues.push(issue);
        else
            truncated = true;
    };
    if (!isPlainObject(data) || !Array.isArray(data.elements)) {
        return { valid: true, issues, truncated };
    }
    const byId = new Map();
    const seenIndices = new Map();
    let previousIndex = null;
    data.elements.forEach((value, index) => {
        if (!isPlainObject(value))
            return;
        const element = value;
        const path = elementPath(index);
        const id = typeof element.id === "string" ? element.id : undefined;
        if (id) {
            const duplicate = byId.get(id);
            if (duplicate) {
                add({
                    code: "duplicate_element_id",
                    path: `${path}.id`,
                    elementId: id,
                    message: `Element ID ${JSON.stringify(id)} is already used at scene.elements[${duplicate.index}].`,
                    repairable: false,
                });
            }
            else {
                byId.set(id, { element, index });
            }
        }
        if (typeof element.index === "string") {
            const duplicateAt = seenIndices.get(element.index);
            if (duplicateAt !== undefined) {
                add({
                    code: "duplicate_order",
                    path: `${path}.index`,
                    elementId: id,
                    message: `Ordering key ${JSON.stringify(element.index)} is already used at scene.elements[${duplicateAt}].`,
                    repairable: true,
                });
            }
            else {
                seenIndices.set(element.index, index);
            }
            if (previousIndex !== null && element.index <= previousIndex) {
                add({
                    code: "invalid_order",
                    path: `${path}.index`,
                    elementId: id,
                    message: "Non-null ordering keys must increase in the same order as the elements array.",
                    repairable: true,
                });
            }
            previousIndex = element.index;
        }
        for (const field of ["seed", "version", "versionNonce", "updated"]) {
            if (field in element && !isSafeInteger(element[field])) {
                add({
                    code: "unsafe_integer",
                    path: `${path}.${field}`,
                    elementId: id,
                    message: `${field} must be a finite safe integer.`,
                    repairable: false,
                });
            }
        }
        if (!activeElement(element))
            return;
        if (element.type === "text" &&
            (typeof element.width !== "number" ||
                element.width <= 0 ||
                typeof element.height !== "number" ||
                element.height <= 0)) {
            add({
                code: "element_will_be_dropped",
                path,
                elementId: id,
                message: "Active text must have positive width and height or Excalidraw cannot render it.",
                repairable: false,
            });
        }
        else if (element.type !== "text" &&
            element.type !== "line" &&
            element.type !== "arrow" &&
            element.type !== "freedraw" &&
            element.width === 0 &&
            element.height === 0) {
            add({
                code: "element_will_be_dropped",
                path,
                elementId: id,
                message: "An active zero-size element would be discarded while opening the drawing.",
                repairable: false,
            });
        }
        if (element.type === "freedraw") {
            const points = Array.isArray(element.points) ? element.points.length : 0;
            if (points < 2) {
                add({
                    code: "element_will_be_dropped",
                    path: `${path}.points`,
                    elementId: id,
                    message: "An active free-draw element needs at least two points.",
                    repairable: false,
                });
            }
            if (element.simulatePressure === false &&
                Array.isArray(element.pressures) &&
                element.pressures.length !== points) {
                add({
                    code: "invalid_freedraw_pressure",
                    path: `${path}.pressures`,
                    elementId: id,
                    message: "Recorded free-draw pressure must contain one value per point.",
                    repairable: false,
                });
            }
        }
        if (element.type === "arrow" &&
            Array.isArray(element.points) &&
            element.points.length === 2 &&
            JSON.stringify(element.points[0]) === JSON.stringify(element.points[1])) {
            add({
                code: "element_will_be_dropped",
                path: `${path}.points`,
                elementId: id,
                message: "An active two-point arrow needs distinct start and end points.",
                repairable: false,
            });
        }
    });
    const hasReciprocalBoundElement = (target, sourceId, sourceType) => Array.isArray(target.boundElements) &&
        target.boundElements.some((bound) => isPlainObject(bound) && bound.id === sourceId && bound.type === sourceType);
    data.elements.forEach((value, index) => {
        if (!isPlainObject(value))
            return;
        const element = value;
        if (!activeElement(element))
            return;
        const path = elementPath(index);
        const id = typeof element.id === "string" ? element.id : undefined;
        if (typeof element.frameId === "string") {
            const frame = byId.get(element.frameId)?.element;
            if (!frame ||
                !activeElement(frame) ||
                (frame.type !== "frame" && frame.type !== "magicframe")) {
                add({
                    code: "invalid_reference",
                    path: `${path}.frameId`,
                    elementId: id,
                    message: "frameId must reference an active frame or magic-frame element.",
                    repairable: false,
                });
            }
            else {
                const visited = new Set(id ? [id] : []);
                let current = frame;
                while (current && typeof current.id === "string") {
                    if (visited.has(current.id)) {
                        add({
                            code: "frame_cycle",
                            path: `${path}.frameId`,
                            elementId: id,
                            message: "Frame membership must not contain a cycle.",
                            repairable: false,
                        });
                        break;
                    }
                    visited.add(current.id);
                    current =
                        typeof current.frameId === "string" ? byId.get(current.frameId)?.element : undefined;
                }
            }
        }
        if (element.type === "text" && typeof element.containerId === "string" && id) {
            const container = byId.get(element.containerId)?.element;
            if (!container || !activeElement(container) || container === element) {
                add({
                    code: "invalid_reference",
                    path: `${path}.containerId`,
                    elementId: id,
                    message: "containerId must reference another active element.",
                    repairable: false,
                });
            }
            else if (!hasReciprocalBoundElement(container, id, "text")) {
                add({
                    code: "binding_mismatch",
                    path: `${path}.containerId`,
                    elementId: id,
                    message: "The text container must list this text in boundElements.",
                    repairable: false,
                });
            }
        }
        if ((element.type === "arrow" || element.type === "line") && id) {
            for (const side of ["startBinding", "endBinding"]) {
                const binding = element[side];
                if (element.type === "line" && binding !== null && binding !== undefined) {
                    add({
                        code: "binding_mismatch",
                        path: `${path}.${side}`,
                        elementId: id,
                        message: "Line elements cannot persist endpoint bindings; use an arrow or null.",
                        repairable: false,
                    });
                    continue;
                }
                if (!isPlainObject(binding) || typeof binding.elementId !== "string")
                    continue;
                const target = byId.get(binding.elementId)?.element;
                if (!target || !activeElement(target) || target === element) {
                    add({
                        code: "invalid_reference",
                        path: `${path}.${side}.elementId`,
                        elementId: id,
                        message: "Arrow bindings must reference another active element.",
                        repairable: false,
                    });
                }
                else if (!hasReciprocalBoundElement(target, id, "arrow")) {
                    add({
                        code: "binding_mismatch",
                        path: `${path}.${side}.elementId`,
                        elementId: id,
                        message: "The bound target must list this arrow in boundElements.",
                        repairable: false,
                    });
                }
            }
        }
        if (Array.isArray(element.boundElements) && id) {
            element.boundElements.forEach((bound, boundIndex) => {
                if (!isPlainObject(bound) || typeof bound.id !== "string")
                    return;
                const target = byId.get(bound.id)?.element;
                const targetType = target?.type;
                if (!target ||
                    !activeElement(target) ||
                    (bound.type !== "text" && bound.type !== "arrow") ||
                    targetType !== bound.type) {
                    add({
                        code: "invalid_reference",
                        path: `${path}.boundElements[${boundIndex}]`,
                        elementId: id,
                        message: "boundElements must reference an active element of the declared type.",
                        repairable: false,
                    });
                }
                else if ((bound.type === "text" && target.containerId !== id) ||
                    (bound.type === "arrow" &&
                        ![target.startBinding, target.endBinding].some((binding) => isPlainObject(binding) && binding.elementId === id))) {
                    add({
                        code: "binding_mismatch",
                        path: `${path}.boundElements[${boundIndex}]`,
                        elementId: id,
                        message: "boundElements and the referenced element must point to each other.",
                        repairable: false,
                    });
                }
            });
        }
        if (element.type === "image" &&
            element.status === "saved" &&
            typeof element.fileId !== "string") {
            add({
                code: "missing_image_file",
                path: `${path}.fileId`,
                elementId: id,
                message: "A saved image must reference a file in scene.files.",
                repairable: false,
            });
        }
        else if (element.type === "image" && typeof element.fileId === "string") {
            const files = isPlainObject(data.files) ? data.files : {};
            if (!isPlainObject(files[element.fileId])) {
                add({
                    code: "missing_image_file",
                    path: `${path}.fileId`,
                    elementId: id,
                    message: `Image file ${JSON.stringify(element.fileId)} is missing from scene.files.`,
                    repairable: false,
                });
            }
        }
    });
    if (isPlainObject(data.files)) {
        for (const [key, file] of Object.entries(data.files)) {
            if (!isPlainObject(file))
                continue;
            const expectedPrefix = typeof file.mimeType === "string" ? `data:${file.mimeType};base64,` : "";
            const encoded = typeof file.dataURL === "string" && file.dataURL.startsWith(expectedPrefix)
                ? file.dataURL.slice(expectedPrefix.length)
                : "";
            if (file.id !== key ||
                !expectedPrefix ||
                !encoded ||
                encoded.length % 4 !== 0 ||
                !/^[A-Za-z0-9+/]*={0,2}$/.test(encoded) ||
                !isSafeInteger(file.created) ||
                file.created < 0) {
                add({
                    code: "invalid_binary_file",
                    path: `scene.files[${JSON.stringify(key)}]`,
                    message: "Binary-file key, ID, MIME type, base64 data URL, and timestamp must agree.",
                    repairable: false,
                });
            }
        }
    }
    return { valid, issues, truncated };
}
//# sourceMappingURL=excalidrawSchema.js.map